import { useCallback, useMemo } from "react"
import { ContentCopy, Refresh, ThumbUp, ThumbUpOffAlt, ThumbDown, ThumbDownOffAlt } from "@mui/icons-material"
import { track } from "helpers/analytics"
import { TRACKING_EVENTS, STATUSES } from "../constants"
import { copySummary } from "../view"

export default function useSummaryButtons({
  isLoading,
  isGenerated,
  thumb,
  summary,
  resource,
  summaryKey,
  localStorageSummary,
  localStorageError,
  setLocalStorageSummary,
  generateSummary,
  error,
  addNotification,
  action,
  setFeedbackOpen,
}) {
  const canRegenerate = useMemo(() => !!(isGenerated || localStorageSummary?.summary || localStorageSummary?.error || summary || error), [isGenerated, localStorageSummary, summary, error])

  const thumbsClick = useCallback((direction) => () => {
    if (thumb === direction) return

    track(TRACKING_EVENTS.FEEDBACK, { direction, action, resource })
    setFeedbackOpen(true)
    if (!localStorageError) {
      setLocalStorageSummary(summaryKey, { ...localStorageSummary, feedback: direction })
    }
    return direction
  }, [thumb, action, resource, localStorageError, setLocalStorageSummary, summaryKey, localStorageSummary, setFeedbackOpen])

  const buttons = useMemo(() => [
    {
      id: "thumbsUp",
      title: "Helpful summary",
      disabled: !isGenerated,
      onClick: thumbsClick("positive"),
      Icon: thumb === "positive" ? ThumbUp : ThumbUpOffAlt,
    }, {
      id: "thumbsDown",
      title: "Unhelpful summary",
      disabled: !isGenerated,
      onClick: thumbsClick("negative"),
      Icon: thumb === "negative" ? ThumbDown : ThumbDownOffAlt,
    },
    {
      id: "copy",
      title: "Copy to clipboard",
      disabled: !isGenerated,
      onClick: () => {
        track(TRACKING_EVENTS.COPY, { action, resource })
        copySummary(summary, resource?.name, addNotification)
      },
      Icon: ContentCopy,
    },
    {
      id: "generate",
      title: "Regenerate summary",
      disabled: isLoading || !canRegenerate,
      onClick: () => {
        generateSummary(TRACKING_EVENTS.REGENERATE, action)
        if (setLocalStorageSummary) {
          setLocalStorageSummary(summaryKey, { status: STATUSES.NOT_STARTED })
        }
      },
      Icon: Refresh,
    },
  ], [canRegenerate, isLoading, thumb, summary, resource, action, addNotification, generateSummary, thumbsClick, isGenerated, summaryKey, setLocalStorageSummary])

  return buttons
}
